import React, { FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import Block from "Components/UI/Block/Block";
import FilterWrapper from "Components/FilterWrapper/FilterWrapper";
import { ICurrentVehicle } from "Shared/Types/vehicle";
import ModalOptions from "Components/UI/ModalFilters/ModalOptions";
import { selectVehicleList } from "Redux/Vehicle/slice";
import { useAppDispatch } from "Redux/store";
import { getVehicleList } from "Redux/Vehicle/reducer";
import DateRange from "Components/UI/DateRange/DateRange";
import {
  getDashboardVehicleAssignment,
  getDashboardVehicleAssignmentDetail,
} from "Redux/Dashboard/reducer";
import ModalFiltersStaticOptions from "Components/UI/ModalFilters/ModalFiltersStaticOptions/ModalFiltersStaticOptions";
import { selectDashboardVehicleAssignmentLoading } from "Redux/Dashboard/slice";
import { LIMIT_VEHICLES } from "Shared/Constants/app";
import CalendarTable from "Components/Dashboard/CalendarTable";
import {
  monthCalendarTableHeads,
  VehicleAssignmentRangeType,
} from "Shared/Constants/dashboard";
import {
  getDateCalendarTableHeads,
  getDaysBetweenDates,
} from "Shared/Utils/dashboard";

import styles from "../styles.module.scss";
import useDashboardVehicleAssignments from "./useDashboardVehicleAssignments";
import useVehicleAssignmentCalendar from "./useVehicleAssignmentCalendar";
import { useCommonDashboardHook } from "../useCommonDashboardHook";
import { SkeletonStatus } from "Components/Dashboard/Skeleton";

type Props = {
  openDetailAssigment: () => void;
  closeDetailAssigment: () => void;
  assignmentId: string | null;
  setAssignmentId: (id: string | null) => void;
};

const VehicleAssignment: FC<Props> = ({
  openDetailAssigment,
  closeDetailAssigment,
  setAssignmentId,
  assignmentId,
}) => {
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const vehicles = useSelector(selectVehicleList) || [];

  const vehicleAssignmentsListLoading = useSelector(
    selectDashboardVehicleAssignmentLoading,
  );

  const {
    params,
    onDateRangeChange,
    onCalendarTypeChange,
    groupedDataByVehicle,
  } = useDashboardVehicleAssignments();

  const { startDate, endDate } = useCommonDashboardHook();

  const { getMonthLogicLine, getDateLogicLine } =
    useVehicleAssignmentCalendar();

  const isYearCalendar = searchParams.has("year");

  const fetchVehicle = (search: string) => {
    return dispatch(
      getVehicleList({ search, limit: LIMIT_VEHICLES, offset: 0 }),
    );
  };

  const assignmentsToRangeParam = searchParams.get("assignments_to");
  const assignmentsFromRangeParam = searchParams.get("assignments_from");
  const assignmentsVehicleParam = searchParams.get("assignment_vehicle");

  useEffect(() => {
    dispatch(getDashboardVehicleAssignment(params));
    closeDetailAssigment();
  }, [
    assignmentsToRangeParam,
    assignmentsFromRangeParam,
    assignmentsVehicleParam,
  ]);

  useEffect(() => {
    if (assignmentId) {
      dispatch(getDashboardVehicleAssignmentDetail(assignmentId));
      openDetailAssigment();
    }
  }, [assignmentId, searchParams]);

  return (
    <Block className={styles.container}>
      <h4 className={styles.container__title}>Vehicle Assignment</h4>
      <FilterWrapper>
        <ModalOptions<ICurrentVehicle>
          options={vehicles}
          fetchOptions={fetchVehicle}
          type="assignment_vehicle"
          title="Vehicle"
          zIndex
        />
        <DateRange
          dateRangeName="assignment_date_range"
          rangeStartName="assignments_from"
          rangeEndName="assignments_to"
          onDateChange={onDateRangeChange}
        />
        <ModalFiltersStaticOptions
          onOptionChange={onCalendarTypeChange}
          option={VehicleAssignmentRangeType}
          filterName="calendar_type"
          isClearable={false}
          title="Range"
        />
      </FilterWrapper>
      {vehicleAssignmentsListLoading && (
        <SkeletonStatus>Loading vehicle assignments…</SkeletonStatus>
      )}
      <CalendarTable
        loading={vehicleAssignmentsListLoading}
        empty="No vehicle assignments in this date range"
        renderCellDate={getDateLogicLine}
        renderCellMonth={getMonthLogicLine}
        onItemClick={setAssignmentId}
        isYearCalendar={isYearCalendar}
        monthColumns={monthCalendarTableHeads}
        dateColumns={getDateCalendarTableHeads(
          getDaysBetweenDates(
            startDate("assignments_from"),
            endDate("assignments_to"),
          ),
        )}
        data={groupedDataByVehicle}
      />
    </Block>
  );
};

export default VehicleAssignment;
